/** True when the browser reports a usable media duration (not 0 / NaN / Infinity). */
export function isValidDuration(duration: number): boolean {
  return Number.isFinite(duration) && duration > 0
}

function durationFromSeekable(audio: HTMLMediaElement): number {
  if (audio.seekable && audio.seekable.length > 0) {
    const end = audio.seekable.end(audio.seekable.length - 1)
    if (isValidDuration(end)) return end
  }
  return 0
}

/**
 * Force a specific media element to compute a finite duration in place.
 * Needed for native `<audio controls>` where duration is read-only and the UI
 * reads from the element itself (MediaRecorder webm/mp4 quirk).
 */
export async function materializeAudioDuration(audio: HTMLAudioElement): Promise<number> {
  if (isValidDuration(audio.duration)) {
    return audio.duration
  }

  const fromSeekable = durationFromSeekable(audio)
  if (fromSeekable > 0) return fromSeekable

  return new Promise((resolve) => {
    let settled = false

    const finish = (duration: number) => {
      if (settled) return
      settled = true
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('seeked', onSeeked)
      window.clearTimeout(timeoutId)
      try {
        audio.currentTime = 0
      } catch {
        // ignore
      }
      resolve(isValidDuration(duration) ? duration : 0)
    }

    const onTimeUpdate = () => {
      if (isValidDuration(audio.duration)) finish(audio.duration)
    }

    const onSeeked = () => {
      if (isValidDuration(audio.duration)) {
        finish(audio.duration)
        return
      }
      const end = durationFromSeekable(audio)
      if (end > 0) finish(end)
    }

    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('seeked', onSeeked)

    const timeoutId = window.setTimeout(() => {
      if (isValidDuration(audio.duration)) {
        finish(audio.duration)
        return
      }
      finish(durationFromSeekable(audio))
    }, 1500)

    try {
      audio.currentTime = 1e101
    } catch {
      finish(0)
    }
  })
}

/**
 * MediaRecorder webm/mp4 blobs often expose `duration === Infinity` until the
 * browser is forced to compute length (common Chromium quirk). Probe on a
 * detached element so we don't disturb the player the user is listening to.
 */
export async function resolveAudioDuration(audio: HTMLAudioElement): Promise<number> {
  if (isValidDuration(audio.duration)) {
    return audio.duration
  }

  const fromSeekable = durationFromSeekable(audio)
  if (fromSeekable > 0) return fromSeekable

  const src = audio.currentSrc || audio.src
  if (!src) return 0

  const probe = new Audio()
  probe.preload = 'auto'

  try {
    await new Promise<void>((resolve, reject) => {
      const onLoaded = () => {
        cleanup()
        resolve()
      }
      const onError = () => {
        cleanup()
        reject(new Error('Failed to load audio for duration probe'))
      }
      const cleanup = () => {
        probe.removeEventListener('loadedmetadata', onLoaded)
        probe.removeEventListener('error', onError)
      }
      probe.addEventListener('loadedmetadata', onLoaded)
      probe.addEventListener('error', onError)
      probe.src = src
      probe.load()
    })
  } catch {
    return 0
  }

  if (isValidDuration(probe.duration)) {
    return probe.duration
  }

  const probeSeekable = durationFromSeekable(probe)
  if (probeSeekable > 0) return probeSeekable

  // Force Chromium to materialize duration for MediaRecorder output
  return new Promise((resolve) => {
    let settled = false

    const finish = (duration: number) => {
      if (settled) return
      settled = true
      probe.removeEventListener('timeupdate', onTimeUpdate)
      probe.removeEventListener('seeked', onSeeked)
      window.clearTimeout(timeoutId)
      probe.removeAttribute('src')
      probe.load()
      resolve(isValidDuration(duration) ? duration : 0)
    }

    const onTimeUpdate = () => {
      if (isValidDuration(probe.duration)) finish(probe.duration)
    }

    const onSeeked = () => {
      if (isValidDuration(probe.duration)) {
        finish(probe.duration)
        return
      }
      const end = durationFromSeekable(probe)
      if (end > 0) finish(end)
    }

    probe.addEventListener('timeupdate', onTimeUpdate)
    probe.addEventListener('seeked', onSeeked)

    const timeoutId = window.setTimeout(() => {
      if (isValidDuration(probe.duration)) {
        finish(probe.duration)
        return
      }
      finish(durationFromSeekable(probe))
    }, 1500)

    try {
      probe.currentTime = 1e101
    } catch {
      finish(0)
    }
  })
}
